import React, { useState, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { chooseAudioMimeType, audioFilename, formatAudioDiagnostics } from '../utils/voiceRecording';

const generateBrainData = () => {
  const nodes = [];
  const links = [];
  const clusters = ['Skill Suites', 'Local Businesses', 'AI Workshop', 'Zubair OS', 'Claude Code', 'SEO Suite', 'Finance Suite', 'Legal Suite'];
  const palette = ['#ebb52f', '#1ea584', '#458cdd', '#8b6acc', '#e87936', '#bd5a98', '#6f91cb', '#d5dbe0'];

  clusters.forEach((name, i) => {
    nodes.push({ id: name, group: i, val: 22, label: name, color: palette[i] });
  });

  for (let i = 0; i < 470; i++) {
    const clusterIndex = i % clusters.length;
    const clusterName = clusters[clusterIndex];
    const nodeId = `node-${i}`;

    nodes.push({
      id: nodeId,
      group: clusterIndex,
      val: 2 + Math.random() * 3,
      label: Math.random() > 0.965 ? `Note ${i}` : '',
      color: palette[clusterIndex]
    });

    links.push({ source: nodeId, target: clusterName });

    if (Math.random() > 0.3) {
      links.push({ source: nodeId, target: `node-${Math.floor(Math.random() * 470)}` });
    }
  }
  return { nodes, links };
};

const JarvisVoice = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('ONLINE');
  const [logs, setLogs] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  const mediaRecorderRef = useRef(null);
  const graphRef = useRef(null);
  const graphWrapRef = useRef(null);
  const recorderStartingRef = useRef(false);
  const voiceRequestInFlightRef = useRef(false);
  const previewURLRef = useRef(null);
  const [previewURL, setPreviewURL] = useState('');
  const [audioDiagnostics, setAudioDiagnostics] = useState('');
  const [graphData] = useState(generateBrainData());
  const [graphSize, setGraphSize] = useState({ width: 900, height: 700 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [search, setSearch] = useState('');
  const [charge, setCharge] = useState(-65);
  const [linkLength, setLinkLength] = useState(65);

  // Configure this public URL in Vercel (never put API keys in React variables).
  const BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const API_URL = `${BASE_URL}/api/voice`;
  const CHAT_URL = `${BASE_URL}/api/chat`;

  useEffect(() => {
    if (!graphWrapRef.current) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setGraphSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(graphWrapRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;
    graph.d3Force('charge')?.strength(charge);
    graph.d3Force('link')?.distance(linkLength);
    graph.d3ReheatSimulation();
  }, [charge, linkLength]);

  // Logic for the Orb colors
  const isProcessing = status === 'PROCESSING...' || status === 'SPEAKING...';

  const addLog = (text, type) => {
    setLogs(prev => [...prev, { text, type }].slice(-6));
  };

  const getReplyText = response => {
    const encoded = response.headers.get('X-Jarvis-Text');
    if (!encoded) return null;
    try { return decodeURIComponent(encoded); }
    catch { return null; }
  };

  useEffect(() => {
    return () => {
      if (previewURLRef.current) URL.revokeObjectURL(previewURLRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.onstop = null; // Unmount must not submit a recording.
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current?.stream?.getTracks().forEach(track => track.stop());
    };
  }, []);

  const replayMicrophoneRecording = () => {
    if (!previewURLRef.current) return;
    // Called from a real user gesture: avoids iOS autoplay restrictions.
    const audio = new Audio(previewURLRef.current);
    audio.play().catch(error => addLog(`> AUDIO PREVIEW: ${error.message}`, 'system-msg'));
  };

  const startRecording = async () => {
    if (recorderStartingRef.current || voiceRequestInFlightRef.current || isSending) return;
    if (!BASE_URL) {
      setStatus('OFFLINE');
      addLog('> SYSTEM: Voice backend is not configured (REACT_APP_API_BASE_URL).', 'system-msg');
      return;
    }
    recorderStartingRef.current = true;
    let stream;
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error('This browser does not support microphone recording.');
      }
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = chooseAudioMimeType(window.MediaRecorder);
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      mediaRecorderRef.current = recorder;
      const startedAt = Date.now();
      let recordingFailed = false;

      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onerror = event => {
        recordingFailed = true;
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        voiceRequestInFlightRef.current = false;
        setStatus('ERROR');
        addLog(`> MICROPHONE: ${event.error?.message || 'Recording failed.'}`, 'system-msg');
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        if (recordingFailed) {
          voiceRequestInFlightRef.current = false;
          return;
        }
        const actualType = recorder.mimeType || chunks.find(chunk => chunk.type)?.type || '';
        const blob = new Blob(chunks, { type: actualType || 'application/octet-stream' });
        void processAudio(blob, Date.now() - startedAt);
      };
      // Single finalized Blob: avoids incomplete MP4 fragments on some Safari versions.
      recorder.start();
      setIsRecording(true);
      setStatus('LISTENING...');
      addLog(`> MICROPHONE: Recording (${recorder.mimeType || 'browser default'})`, 'system-msg');
    } catch (error) {
      stream?.getTracks().forEach(track => track.stop());
      setStatus('ERROR');
      addLog(`> MICROPHONE: ${error.message}`, 'system-msg');
    } finally {
      recorderStartingRef.current = false;
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== 'recording' || voiceRequestInFlightRef.current) return;
    voiceRequestInFlightRef.current = true;
    setIsRecording(false);
    setStatus('PROCESSING...');
    try {
      recorder.stop(); // onstop finalizes the blob before tracks are released.
    } catch (error) {
      recorder.stream?.getTracks().forEach(track => track.stop());
      voiceRequestInFlightRef.current = false;
      setStatus('ERROR');
      addLog(`> MICROPHONE: ${error.message}`, 'system-msg');
    }
  };

  const toggleRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') stopRecording();
    else void startRecording();
  };

  const processAudio = async (audioBlob, durationMs) => {
    const metadata = formatAudioDiagnostics(audioBlob, durationMs);
    setAudioDiagnostics(metadata);
    if (previewURLRef.current) URL.revokeObjectURL(previewURLRef.current);
    const url = URL.createObjectURL(audioBlob);
    previewURLRef.current = url;
    setPreviewURL(url);

    if (audioBlob.size < 1024 || durationMs < 500) {
      setStatus('ERROR');
      addLog(`> MICROPHONE: Too little audio (${metadata}). Try holding for 3–5 seconds.`, 'system-msg');
      voiceRequestInFlightRef.current = false;
      return;
    }

    const filename = audioFilename(audioBlob.type);
    if (filename === 'recording.bin') {
      setStatus('ERROR');
      addLog(`> MICROPHONE: Unrecognized audio format (${audioBlob.type}). Upload stopped.`, 'system-msg');
      voiceRequestInFlightRef.current = false;
      return;
    }
    const formData = new FormData();
    formData.append('audio', audioBlob, filename);
    setStatus('PROCESSING...');
    addLog(`> AUDIO: ${metadata} -> ${filename}. Sending to voice API.`, 'user-msg');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers: {
          'ngrok-skip-browser-warning': 'true',
          'X-Tunnel-Skip-AntiPhishing-Page': 'true',
          'Accept': 'audio/mpeg, application/json',
        },
      });

      if (!response.ok) {
        const bodyText = (await response.text()).slice(0, 350).replace(/\s+/g, ' ');
        throw new Error(`Voice API HTTP ${response.status}: ${bodyText || 'no response details'}`);
      }
      const contentType = response.headers.get('content-type') || '';
      if (contentType.startsWith('audio/')) {
        setStatus('SPEAKING...');
        addLog(`> JARVIS: ${getReplyText(response) || '[audio reply]'}`, 'jarvis-msg');
        const replyBlob = await response.blob();
        const replyUrl = URL.createObjectURL(replyBlob);
        const audio = new Audio(replyUrl);
        audio.onended = () => {
          setStatus('ONLINE');
          URL.revokeObjectURL(replyUrl);
        };
        audio.onerror = () => {
          setStatus('ERROR');
          URL.revokeObjectURL(replyUrl);
          addLog('> JARVIS: The response audio could not be played.', 'system-msg');
        };
        try {
          await audio.play();
        } catch (error) {
          URL.revokeObjectURL(replyUrl);
          throw new Error(`Audio playback: ${error.message}`);
        }
      } else if (contentType.includes('json')) {
        const data = await response.json();
        addLog(`> JARVIS: ${data.text || data.error || 'No recognized speech'}`, 'jarvis-msg');
        if (data.text) {
          setStatus('SPEAKING...');
          const utterance = new SpeechSynthesisUtterance(data.text);
          utterance.onend = () => setStatus('ONLINE');
          utterance.onerror = () => setStatus('ERROR');
          window.speechSynthesis.speak(utterance);
        } else {
          setStatus('ONLINE');
        }
      } else {
        throw new Error(`Unexpected API content-type: ${contentType || 'missing'}`);
      }
    } catch (error) {
      setStatus('ERROR');
      addLog(`> SYSTEM: ${error.name === 'AbortError' ? 'Voice API timed out.' : error.message}`, 'system-msg');
    } finally {
      clearTimeout(timeoutId);
      voiceRequestInFlightRef.current = false;
    }
  };

  const sendText = async (e) => {
    e.preventDefault();
    const msg = textInput.trim();
    if (!msg || isSending) return;


    setTextInput('');
    setIsSending(true);
    setStatus('PROCESSING...');
    addLog(`> YOU: ${msg}`, 'user-msg');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      const response = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'audio/mpeg, application/json',
          'X-Tunnel-Skip-AntiPhishing-Page': 'true',
        },
        body: JSON.stringify({ text: msg }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Backend returned HTTP ${response.status}`);

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('audio')) {
        setStatus('SPEAKING...');
        addLog(`> JARVIS: ${getReplyText(response) || '[audio reply]'}`, 'jarvis-msg');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => {
          setStatus('ONLINE');
          URL.revokeObjectURL(url);
        };
        await audio.play();
      } else {
        const data = await response.json();
        const reply = typeof data.text === 'string' ? data.text.trim() : '';

        // Some Jarvis backends return HTTP 200 with an LLM failure as text.
        // Do not present or speak that error as a successful AI answer.
        if (data.error || /^LLM error\b/i.test(reply)) {
          setStatus('ERROR');
          addLog(
            '> SYSTEM: Jarvis AI backend is temporarily unavailable.',
            'system-msg'
          );
          return;
        }

        if (!reply) {
          throw new Error('Jarvis returned an empty response.');
        }

        addLog(`> JARVIS: ${reply}`, 'jarvis-msg');
        setStatus('SPEAKING...');
        const u = new SpeechSynthesisUtterance(reply);
        u.onend = () => setStatus('ONLINE');
        u.onerror = () => setStatus('ERROR');
        window.speechSynthesis.speak(u);
      }
    } catch (err) {
      setStatus('ERROR');
      addLog(
        `> SYSTEM: ${err.name === 'AbortError' ? 'Request timed out.' : err.message}`,
        'system-msg'
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="jarvis-dashboard">
      {/* LEFT SIDEBAR */}
      <div className="sidebar left-sidebar d-none d-lg-flex">
        <div className="panel-header mb-4">
          <h2>AI WORKSHOP OS</h2>
          <p className="muted-text">545 notes · 1954 connections</p>
        </div>

        <div className="search-box mb-4">
          <input type="text" className="form-control" placeholder="Search the brain..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="panel-section mb-4">
          <h3>INSPECTOR</h3>
          <p className="muted-text">{selectedNode ? `Focused: ${selectedNode.id}` : 'Click a node to focus it and explore its connections.'}</p>
          {search && <button className="control-btn" onClick={() => { const node = graphData.nodes.find(n => n.id.toLowerCase().includes(search.toLowerCase())); if (node) { setSelectedNode(node); graphRef.current?.centerAt(node.x, node.y, 500); graphRef.current?.zoom(2, 500); } }}>Focus match</button>}
        </div>

        <div className="panel-section">
          <h3>TOP HUBS</h3>
          <ul className="hub-list list-unstyled">
            <li className="mb-2"><span className="dot yellow"></span> Skill Suites <span className="count">236</span></li>
            <li className="mb-2"><span className="dot yellow"></span> Local Businesses <span className="count">123</span></li>
            <li className="mb-2"><span className="dot yellow"></span> AI Workshop <span className="count">82</span></li>
            <li><span className="dot blue"></span> Zubair OS <span className="count">51</span></li>
          </ul>
        </div>
        <div className="panel-section mt-4"><h3>FORCES</h3><label className="force-label">Repel <input type="range" min="-140" max="-15" value={charge} onChange={e => setCharge(+e.target.value)} /></label><label className="force-label">Link length <input type="range" min="25" max="130" value={linkLength} onChange={e => setLinkLength(+e.target.value)} /></label></div>
      </div>

      {/* CENTER CANVAS */}
      <div className="center-canvas">
        <div className="canvas-grid"></div>

        <div className="top-controls">
          <button className="control-btn" onClick={() => graphRef.current?.zoomToFit(600, 60)}>⊙ Fit</button>
          <button className="control-btn" onClick={() => { setSelectedNode(null); graphRef.current?.zoomToFit(600, 60); }}>↻</button>
          <button type="button" className="control-btn" disabled={!previewURL} onClick={replayMicrophoneRecording} title="Play last microphone recording locally" aria-label="Play back my last microphone recording">▶ Mic test</button>
        </div>

        <div className="graph-container" ref={graphWrapRef}>
          <ForceGraph2D
            ref={graphRef}
            graphData={graphData}
            nodeColor={node => node.color}
            nodeRelSize={4}
            linkColor={() => 'rgba(95,142,156,0.18)'}
            linkWidth={0.5}
            backgroundColor="#050709"
            nodeLabel={node => node.label || node.id}
            onNodeClick={node => { setSelectedNode(node); graphRef.current?.centerAt(node.x, node.y, 600); graphRef.current?.zoom(2.1, 600); }}
            onEngineStop={() => graphRef.current?.zoomToFit(600, 80)}
            width={graphSize.width}
            height={graphSize.height}
          />
        </div>

        {/* TEXT CHAT BAR */}
        <form className="text-chat-bar" onSubmit={sendText}>
          <input
            type="text"
            className="form-control text-chat-input"
            placeholder="Type to J.A.R.V.I.S..."
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            disabled={isSending}
          />
          <button
            type="submit"
            className="control-btn text-chat-send"
            disabled={isSending || !textInput.trim()}
          >
            {isSending ? '…' : 'Send'}
          </button>
        </form>

        <div className="terminal-log">
          {audioDiagnostics && <p className="system-msg">Mic capture: {audioDiagnostics}</p>}
          {logs.length === 0 ? (
            <p className="muted-text text-center">"Monday's got three items, sir: 'DFY Service Launch: Team Sync' at 11 AM..."</p>
          ) : (
            logs.map((log, i) => <p key={i} className={log.type}>{log.text}</p>)
          )}
        </div>
      </div>

      {/* RIGHT SIDEBAR */}
      <div className="sidebar right-sidebar d-none d-lg-flex">
        <div className="filter-section mb-4">
          <h3>FILTER</h3>
          <ul className="hub-list list-unstyled">
            <li className="mb-2"><span className="dot green"></span> Router <span className="count">1</span></li>
            <li className="mb-2"><span className="dot green"></span> Wiki <span className="count">12</span></li>
            <li className="mb-2"><span className="dot orange"></span> Concepts <span className="count">24</span></li>
            <li><span className="dot blue"></span> Skills <span className="count">197</span></li>
          </ul>
        </div>

        <div className="orb-container desktop-orb mt-4">
          <div className={`jarvis-orb ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`} onClick={toggleRecording}>
            <div className="orb-ring-outer"></div>
            <div className="orb-ring-inner"></div>
            <div className="orb-text">J.A.R.V.I.S.</div>
          </div>

          <div className="status-badges mt-4">
            <span className="badge online">● {status}</span>
            <span className="badge model">◇ JARVIS VOICE</span>
          </div>
        </div>

        <div className="toggle-switches mt-auto">
          <div className="toggle-row"><span>EYES</span> <span className="muted-text">off</span></div>
          <div className="toggle-row"><span>HOLO</span> <span className="muted-text">off</span></div>
        </div>

        <div className="bottom-right-text text-end mt-3">
          <p>• {status}</p>
          <p>gcal_events</p>
        </div>
      </div>

      {/* MOBILE FLOATING ORB */}
      <div className="mobile-orb-container d-flex d-lg-none">
        <div className={`jarvis-orb ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`} onClick={toggleRecording}>
          <div className="orb-ring-outer"></div>
          <div className="orb-ring-inner"></div>
          <div className="orb-text" style={{ fontSize: '0.7rem' }}>J.A.R.V.I.S.</div>
        </div>
      </div>
    </div>
  );
};

export default JarvisVoice;
